import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS } from '../config/constants';
import TriggersConfigTab from './TriggersConfigTab';
import './CRMListConfigModal.css';
import AppIcon from './icons/AppIcon';

export default function CRMEquipeVendasConfigModal({ list, onClose, onSave, projectId }) {
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [connection, setConnection] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [toolEnabled, setToolEnabled] = useState(false);
  const fileInputRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const MAX_IMAGES = 3;
  const MAX_IMAGE_SIZE = 3 * 1024 * 1024;

  const [initialAudioEnabled, setInitialAudioEnabled] = useState(false);
  const [initialAudioUrl, setInitialAudioUrl] = useState('');
  const [audioPreviewUrl, setAudioPreviewUrl] = useState('');
  const [blockAgentResponseForInitialAudio, setBlockAgentResponseForInitialAudio] = useState(true);
  const [audioRecording, setAudioRecording] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioObjectUrlRef = useRef(null);

  const [initialMessageEnabled, setInitialMessageEnabled] = useState(false);
  const [initialMessage, setInitialMessage] = useState('');
  const [blockAgentResponse, setBlockAgentResponse] = useState(true);

  // Estados para formulário
  const [formEnabled, setFormEnabled] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldType, setNewFieldType] = useState('text'); // text, datetime

  // Estados para configurações de agendamento
  const [scheduleSettings, setScheduleSettings] = useState({
    enabled: false,
    startTime: '08:00',
    endTime: '18:00',
    maxConcurrent: 1,
    durationMinutes: 30,
    workDays: 'mon-fri' // mon-fri, mon-sat, mon-sun, all
  });

  // Estado para habilitação de Gatilhos (apenas Equipe de Vendas)
  const [triggersEnabled, setTriggersEnabled] = useState(false);

  // Estados para notificação em grupo
  const [groupNotificationEnabled, setGroupNotificationEnabled] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Opções de dias de atendimento
  const WORK_DAYS_OPTIONS = [
    { value: 'mon-fri', label: 'Segunda a Sexta' },
    { value: 'mon-sat', label: 'Segunda a Sábado' },
    { value: 'mon-sun', label: 'Segunda a Domingo' },
    { value: 'all', label: 'Todos os dias' }
  ];

  // Opções de tipo de campo
  const FIELD_TYPES = [
    { value: 'text', label: 'Texto', icon: 'text_fields' },
    { value: 'datetime', label: 'Agendamento (Data/Hora)', icon: 'schedule' }
  ];

  // Carregar dados atualizados da lista do banco de dados (apenas uma vez)
  useEffect(() => {
    const loadListData = async () => {
      if (!list?.id || hasLoadedRef.current) return;
      
      hasLoadedRef.current = true;
      
      try {
        const { data: updatedList, error } = await supabase
          .from('project_lists')
          .select('metadata, automation_rules')
          .eq('id', list.id)
          .single();
        
        if (error) {
          console.error('Erro ao carregar lista:', error);
          return;
        }
        
        // Atualizar estados com dados do banco
        const loadedImages = [];
        if (updatedList?.metadata?.initial_images && Array.isArray(updatedList.metadata.initial_images)) {
          loadedImages.push(...updatedList.metadata.initial_images);
        } else if (updatedList?.metadata?.initial_image) {
          loadedImages.push(updatedList.metadata.initial_image);
        }
        
        setImages(loadedImages);
        
        if (updatedList?.metadata?.enabled !== undefined) {
          setToolEnabled(updatedList.metadata.enabled);
        }

        if (updatedList?.metadata?.initial_audio_enabled !== undefined) {
          setInitialAudioEnabled(!!updatedList.metadata.initial_audio_enabled);
        }
        if (updatedList?.metadata?.initial_audio_url) {
          setInitialAudioUrl(updatedList.metadata.initial_audio_url);
        }
        if (updatedList?.metadata?.initial_audio_block_agent_response !== undefined) {
          setBlockAgentResponseForInitialAudio(updatedList.metadata.initial_audio_block_agent_response !== false);
        }

        // Carregar configurações de notificação em grupo
        const groupNotifConfig = updatedList?.metadata?.group_notification;
        if (groupNotifConfig) {
          setGroupNotificationEnabled(!!groupNotifConfig.enabled);
          if (groupNotifConfig.group_jid && groupNotifConfig.group_name) {
            setSelectedGroup({
              jid: groupNotifConfig.group_jid,
              name: groupNotifConfig.group_name
            });
          }
        }

        const automationRules = updatedList?.automation_rules || {};
        setInitialMessageEnabled(automationRules.initial_message_enabled || false);
        setInitialMessage(automationRules.initial_message || '');
        setBlockAgentResponse(automationRules.block_agent_response !== false);

        // Carregar configurações de Gatilhos PRIMEIRO (apenas Equipe de Vendas)
        const triggersEnabledValue = !!automationRules.triggers_enabled;
        if (automationRules.triggers_enabled !== undefined) {
          setTriggersEnabled(triggersEnabledValue);
        }

        // Carregar configurações de formulário
        if (automationRules.form_config) {
          setFormEnabled(!!automationRules.form_config.enabled);
          const fields = Array.isArray(automationRules.form_config.fields) 
            ? automationRules.form_config.fields 
            : [];
          setFormFields(fields);
        } else {
          loadFormFieldsFromProject();
        }
      } catch (error) {
        console.error('Erro ao carregar dados da lista:', error);
      }
    };
    
    loadListData();
  }, [list?.id, projectId]);

  // Validar e habilitar/desabilitar Gatilhos (apenas Equipe de Vendas)
  const handleTriggersToggle = (newValue) => {
    const isEquipeVendas = list?.name === 'Equipe de Vendas';
    
    if (!isEquipeVendas) {
      // Se não for Equipe de Vendas, permite habilitar sem validações
      setTriggersEnabled(newValue);
      return;
    }

    // Para Equipe de Vendas: validar conflitos
    if (newValue && (formEnabled || scheduleSettings.enabled)) {
      alert('⚠️ Gatilhos não podem ser habilitados simultaneamente com Formulário ou Agendamento.\n\nDesabilite Formulário ou Agendamento primeiro.');
      return;
    }

    setTriggersEnabled(newValue);
  };

  // Validar ao habilitar Formulário (apenas Equipe de Vendas)
  const handleFormToggle = (newValue) => {
    const isEquipeVendas = list?.name === 'Equipe de Vendas';
    
    if (!isEquipeVendas) {
      setFormEnabled(newValue);
      return;
    }

    if (newValue && triggersEnabled) {
      alert('⚠️ Formulário não pode ser habilitado simultaneamente com Gatilhos.\n\nDesabilite Gatilhos primeiro.');
      return;
    }

    setFormEnabled(newValue);
  };

  // Validar ao habilitar Agendamento (apenas Equipe de Vendas)
  const handleScheduleToggle = (newValue) => {
    const isEquipeVendas = list?.name === 'Equipe de Vendas';
    
    if (!isEquipeVendas) {
      setScheduleSettings({ ...scheduleSettings, enabled: newValue });
      return;
    }

    if (newValue && triggersEnabled) {
      alert('⚠️ Agendamento não pode ser habilitado simultaneamente com Gatilhos.\n\nDesabilite Gatilhos primeiro.');
      return;
    }

    setScheduleSettings({ ...scheduleSettings, enabled: newValue });
  };

  // Carregar campos do formulário do projeto
  const loadFormFieldsFromProject = async () => {
    try {
      const { data: projectData, error } = await supabase
        .from('projects')
        .select('settings')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Erro ao carregar projeto:', error);
        return;
      }

      const projectFormFields = projectData?.settings?.crm?.formFields || [];
      
      if (projectFormFields.length > 0) {
        setFormEnabled(true);
        setFormFields(projectFormFields);
        
        // Carregar configurações de agendamento
        const scheduleConfig = projectData?.settings?.crm?.scheduleSettings;
        if (scheduleConfig) {
          setScheduleSettings(scheduleConfig);
        }
      }

      // Carregar configurações de notificação em grupo da lista
      if (list?.id) {
        const { data: listData } = await supabase
          .from('project_lists')
          .select('metadata')
          .eq('id', list.id)
          .single();
        
        const groupNotifConfig = listData?.metadata?.group_notification;
        if (groupNotifConfig) {
          setGroupNotificationEnabled(!!groupNotifConfig.enabled);
          if (groupNotifConfig.group_jid && groupNotifConfig.group_name) {
            setSelectedGroup({
              jid: groupNotifConfig.group_jid,
              name: groupNotifConfig.group_name
            });
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar campos do projeto:', error);
    }
  };

  useEffect(() => {
    return () => {
      try {
        if (audioObjectUrlRef.current) {
          URL.revokeObjectURL(audioObjectUrlRef.current);
          audioObjectUrlRef.current = null;
        }
      } catch (_) {}
    };
  }, []);

  const getAudioMimeType = () => {
    try {
      const candidates = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg'
      ];
      for (const c of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) {
          return c;
        }
      }
    } catch (_) {}
    return '';
  };

  const parseStorageRefFromPublicUrl = (url) => {
    try {
      if (!url || typeof url !== 'string') return null;
      const m = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
      if (m) return { bucket: m[1], path: m[2] };
      return null;
    } catch (_) {
      return null;
    }
  };

  const uploadRecordedAudio = async (blob) => {
    try {
      setAudioUploading(true);
      const user = JSON.parse(localStorage.getItem('astro_user_data') || '{}');
      if (!user?.company_id) throw new Error('Usuário sem company_id');

      const MAX_AUDIO_SIZE = 10 * 1024 * 1024;
      if (blob.size > MAX_AUDIO_SIZE) {
        throw new Error('Áudio muito grande. Limite: 10MB');
      }

      try {
        if (initialAudioUrl) {
          const ref = parseStorageRefFromPublicUrl(initialAudioUrl);
          if (ref) {
            await supabase.storage.from(ref.bucket).remove([ref.path]);
          }
        }
      } catch (_) {}

      const bucket = 'project-attachments';
      const ext = (blob.type || '').includes('ogg') ? 'ogg' : 'webm';
      const fileName = `crm-initial-audio/${user.company_id}/${projectId}/${list?.id || 'unknown'}/${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from(bucket)
        .upload(fileName, blob, {
          cacheControl: '3600',
          upsert: true,
          contentType: blob.type || 'audio/webm'
        });

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      const { data: urlData } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl || '';
      if (!publicUrl) throw new Error('Falha ao obter URL pública do áudio');
      setInitialAudioUrl(publicUrl);
      setInitialAudioEnabled(true);
      setAudioPreviewUrl(publicUrl);
    } catch (error) {
      console.error('Erro ao fazer upload do áudio:', error);
      alert('Erro ao salvar áudio: ' + error.message);
    } finally {
      setAudioUploading(false);
    }
  };

  const handleRemoveAudio = async () => {
    try {
      if (!initialAudioUrl) {
        setInitialAudioEnabled(false);
        return;
      }
      const ref = parseStorageRefFromPublicUrl(initialAudioUrl);
      if (ref) {
        await supabase.storage.from(ref.bucket).remove([ref.path]);
      }
      setInitialAudioUrl('');
      setInitialAudioEnabled(false);
      setAudioPreviewUrl('');
    } catch (error) {
      console.error('Erro ao remover áudio:', error);
      alert('Erro ao remover áudio: ' + error.message);
    }
  };

  useEffect(() => {
    const loadConnection = async () => {
      try {
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings, project_type')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;

        // Buscar conexão conforme tipo de projeto
        let connectionId = null;
        if (project?.project_type === 'crm_multi') {
          const connectionIds = project?.settings?.crm_multi?.connection_ids || [];
          connectionId = connectionIds[0] || null; // Usa a primeira conexão
        } else if (project?.project_type === 'marketing_campaign') {
          const connectionIds = project?.settings?.marketing_campaign?.connection_ids || [];
          connectionId = connectionIds[0] || null; // Usa a primeira conexão
        } else {
          connectionId = project?.settings?.crm?.connection_id;
        }

        if (connectionId) {
          const { data: conn, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .single();

          if (connError) throw connError;
          setConnection(conn);
        }
      } catch (error) {
        console.error('Erro ao carregar conexão:', error);
      }
    };

    if (projectId) {
      loadConnection();
    }
  }, [projectId]);

  const startAudioRecording = async () => {
    try {
      if (audioRecording) return;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = getAudioMimeType();
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mr.onstop = async () => {
        try {
          stream.getTracks().forEach(t => t.stop());
        } catch (_) {}

        const chunks = audioChunksRef.current || [];
        audioChunksRef.current = [];
        if (chunks.length === 0) return;

        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        if (blob.size <= 0) return;

        try {
          if (audioObjectUrlRef.current) {
            URL.revokeObjectURL(audioObjectUrlRef.current);
            audioObjectUrlRef.current = null;
          }
          audioObjectUrlRef.current = URL.createObjectURL(blob);
          setAudioPreviewUrl(audioObjectUrlRef.current);
        } catch (_) {}

        await uploadRecordedAudio(blob);
      };

      mediaRecorderRef.current = mr;
      mr.start();
      setAudioRecording(true);
    } catch (error) {
      console.error('Erro ao iniciar gravação de áudio:', error);
      alert('Erro ao acessar microfone. Verifique as permissões do navegador.');
      setAudioRecording(false);
    }
  };

  const stopAudioRecording = async () => {
    try {
      const mr = mediaRecorderRef.current;
      if (!mr) return;
      if (mr.state === 'inactive') return;
      mr.stop();
      setAudioRecording(false);
      mediaRecorderRef.current = null;
    } catch (error) {
      console.error('Erro ao parar gravação de áudio:', error);
      setAudioRecording(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (images.length + files.length > MAX_IMAGES) {
      alert(`Máximo de ${MAX_IMAGES} imagens permitidas. Você já tem ${images.length} imagem(ns).`);
      return;
    }

    const newImages = [];
    for (const file of files) {
      if (file.size > MAX_IMAGE_SIZE) {
        alert(`A imagem "${file.name}" deve ter no máximo 3MB`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        alert(`"${file.name}" não é uma imagem válida`);
        continue;
      }

      await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newImages.push(reader.result);
          resolve();
        };
        reader.onerror = () => {
          alert(`Erro ao processar "${file.name}"`);
          reject();
        };
        reader.readAsDataURL(file);
      });
    }

    if (newImages.length > 0) {
      setImages(prevImages => [...prevImages, ...newImages]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // Funções para gerenciar campos do formulário
  const handleAddField = () => {
    if (!newFieldName.trim()) {
      alert('Por favor, preencha o nome do campo');
      return;
    }

    const newField = {
      id: Date.now(),
      name: newFieldName,
      type: newFieldType, // 'text' ou 'datetime'
      icon: newFieldType === 'datetime' ? 'schedule' : 'label',
      required: newFieldRequired
    };

    setFormFields([...formFields, newField]);
    setNewFieldName('');
    setNewFieldRequired(false);
    setNewFieldType('text'); // Reset para texto
  };

  const handleRemoveField = (fieldId) => {
    setFormFields(formFields.filter(f => f.id !== fieldId));
  };

  // Função para carregar grupos do WhatsApp
  const loadGroups = async () => {
    if (!projectId) {
      console.error('[loadGroups] projectId não definido');
      return;
    }
    
    setLoadingGroups(true);
    try {
      // Buscar connection_id do projeto
      const { data: projectData, error: projectError } = await supabase
        .from('projects')
        .select('settings, project_type')
        .eq('id', projectId)
        .single();

      if (projectError) {
        alert('Erro ao buscar projeto: ' + projectError.message);
        return;
      }

      // Buscar conexão conforme tipo de projeto
      let connectionId = null;
      if (projectData?.project_type === 'crm_multi') {
        const connectionIds = projectData?.settings?.crm_multi?.connection_ids || [];
        connectionId = connectionIds[0] || null; // Usa a primeira conexão
      } else if (projectData?.project_type === 'marketing_campaign') {
        const connectionIds = projectData?.settings?.marketing_campaign?.connection_ids || [];
        connectionId = connectionIds[0] || null; // Usa a primeira conexão
      } else {
        connectionId = projectData?.settings?.crm?.connection_id;
      }

      if (!connectionId) {
        alert('Conexão WhatsApp não configurada neste projeto. Configure a conexão primeiro.');
        return;
      }

      // Chamar API para listar grupos
      const backendUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL) ? import.meta.env.VITE_BACKEND_URL : 'https://api.halo.ia.br';
      const response = await fetch(`${backendUrl}/connections/groups/${connectionId}?noparticipants=true`);
      const data = await response.json();

      if (!response.ok) {
        alert('Erro ao carregar grupos: ' + (data.error || 'Erro desconhecido'));
        setAvailableGroups([]);
        return;
      }

      if (data.groups && Array.isArray(data.groups)) {
        setAvailableGroups(data.groups);
      } else {
        setAvailableGroups([]);
      }
    } catch (error) {
      alert('Erro ao carregar grupos: ' + error.message);
      setAvailableGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Função para selecionar um grupo

  const handleToggleFieldRequired = (fieldId) => {
    setFormFields(formFields.map(f =>
      f.id === fieldId ? { ...f, required: !f.required } : f
    ));
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const listData = {
        ...list,
        metadata: {
          ...list?.metadata,
          initial_images: images,
          enabled: toolEnabled,
          initial_audio_enabled: initialAudioEnabled,
          initial_audio_url: initialAudioUrl || null,
          initial_audio_block_agent_response: blockAgentResponseForInitialAudio,
          group_notification: {
            enabled: groupNotificationEnabled,
            group_jid: selectedGroup?.jid || null,
            group_name: selectedGroup?.name || null
          }
        }
      };

      // Buscar automation_rules atual para preservar triggers já salvos
      const { data: listaAtual } = await supabase
        .from('project_lists')
        .select('automation_rules')
        .eq('id', list.id)
        .single();

      const automationRules = {
        ...listaAtual?.automation_rules,
        initial_message_enabled: initialMessageEnabled,
        initial_message: initialMessage,
        block_agent_response: blockAgentResponse,
        triggers_enabled: triggersEnabled
      };

      // 1. Salvar configurações da lista (sem form_config)
      const { error: listError } = await supabase
        .from('project_lists')
        .update({
          metadata: listData.metadata,
          automation_rules: automationRules
        })
        .eq('id', list.id)
        .eq('project_id', projectId);

      if (listError) throw listError;

      // 2. Salvar formulário no projeto (único lugar correto)
      const { data: projectData, error: projectFetchError } = await supabase
        .from('projects')
        .select('settings')
        .eq('id', projectId)
        .single();

      if (projectFetchError) throw projectFetchError;

      const updatedSettings = {
        ...projectData.settings,
        crm: {
          ...projectData.settings?.crm,
          formFields: formFields,
          scheduleSettings: scheduleSettings
        }
      };

      const { error: projectUpdateError } = await supabase
        .from('projects')
        .update({ settings: updatedSettings })
        .eq('id', projectId);

      if (projectUpdateError) throw projectUpdateError;

      console.log('✅ Configurações salvas com sucesso!');
      alert('Configurações salvas com sucesso!');
      onSave(listData);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay-crm-list" onClick={onClose}>
      <div className="crm-list-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="crm-modal-header">
          <div className="crm-modal-title-section">
            <AppIcon name="settings" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="crm-modal-content">
          {/* Seção Principal */}
          <div className="crm-modal-main">
            {/* Seção de Habilitação da Ferramenta */}
            <div className="crm-section">
              <h4>
                <AppIcon name="power_settings_new" />
                Habilitar envio de imagem
              </h4>
              
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={toolEnabled}
                    onChange={(e) => setToolEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {toolEnabled 
                      ? '✅ Ferramenta ativa' 
                      : '❌ Ferramenta inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {toolEnabled 
                    ? 'Imagem será enviada para todos os novos contatos nesta lista'
                    : 'Nenhuma imagem será enviada para novos contatos'}
                </small>
              </div>
            </div>

            {/* Upload de Imagens */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="image" />
                  <h4>Enviar Imagem Inicial ({images.length}/{MAX_IMAGES})</h4>
                </div>
              </div>

              <div className="image-upload-container">
                <div className="upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="image-input"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={images.length >= MAX_IMAGES}
                    style={{ display: 'none' }}
                  />
                  <label 
                    htmlFor="image-input" 
                    className="upload-label"
                    style={{ opacity: images.length >= MAX_IMAGES ? 0.5 : 1, cursor: images.length >= MAX_IMAGES ? 'not-allowed' : 'pointer' }}
                  >
                    <AppIcon name="cloud_upload" />
                    <span>{images.length >= MAX_IMAGES ? 'Limite de imagens atingido' : 'Clique para selecionar imagens'}</span>
                    <small>ou arraste e solte aqui</small>
                  </label>
                </div>

                {/* Previews das Imagens */}
                {images.length > 0 && (
                  <div className="images-preview-container">
                    <div className="preview-label">Previews ({images.length}):</div>
                    <div className="images-grid">
                      {images.map((image, index) => (
                        <div key={index} className="image-preview-item">
                          <img src={image} alt={`preview-${index}`} />
                          <div className="image-number">{index + 1}</div>
                          <button
                            className="btn-remove-image-small"
                            onClick={() => handleRemoveImage(index)}
                            type="button"
                            title="Remover imagem"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="image-info">
                <p>
                  <strong>Formatos suportados:</strong> JPG, PNG, GIF, WebP
                </p>
                <p>
                  <strong>Tamanho máximo por imagem:</strong> 3MB
                </p>
                <p>
                  <strong>Máximo de imagens:</strong> {MAX_IMAGES}
                </p>
              </div>
            </div>

            {/* Seção de Mensagem Inicial Fixa */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="chat" />
                  <h4>Mensagem Inicial Fixa</h4>
                </div>
              </div>

              {/* Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={initialMessageEnabled}
                    onChange={(e) => setInitialMessageEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {initialMessageEnabled 
                      ? '✅ Mensagem inicial ativa' 
                      : '❌ Mensagem inicial inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {initialMessageEnabled 
                    ? 'Uma mensagem fixa será enviada quando um contato entrar nesta lista'
                    : 'Nenhuma mensagem fixa será enviada'}
                </small>
              </div>

              {/* Configuração da Mensagem */}
              {initialMessageEnabled && (
                <div className="initial-message-config">
                  <div className="form-group">
                    <label>Mensagem a ser enviada:</label>
                    <textarea
                      value={initialMessage}
                      onChange={(e) => setInitialMessage(e.target.value)}
                      placeholder="Digite a mensagem que será enviada automaticamente..."
                      rows={4}
                      className="initial-message-textarea"
                    />
                  </div>

                  {/* Preview da Mensagem */}
                  {initialMessage && (
                    <div className="message-preview">
                      <label>Preview:</label>
                      <div className="preview-box">
                        {initialMessage}
                      </div>
                    </div>
                  )}

                  {/* Toggle Bloquear Agente */}
                  <div className="block-agent-section">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={blockAgentResponse}
                        onChange={(e) => setBlockAgentResponse(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-text">
                        Bloquear resposta do agente IA
                      </span>
                    </label>
                    <small className="toggle-description">
                      {blockAgentResponse 
                        ? '⚠️ O agente NÃO responderá quando esta mensagem for enviada.'
                        : '✅ O agente responderá normalmente após esta mensagem ser enviada.'}
                    </small>
                  </div>
                </div>
              )}
            </div>

            {/* Seção de Áudio */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="mic" />
                  <h4>Enviar áudio inicial</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={initialAudioEnabled}
                    onChange={(e) => {
                      const next = e.target.checked;
                      if (next && !initialAudioUrl) {
                        alert('Grave e salve um áudio antes de habilitar.');
                        return;
                      }
                      setInitialAudioEnabled(next);
                    }}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {initialAudioEnabled ? '✅ Áudio ativo' : '❌ Áudio inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {initialAudioUrl
                    ? 'Áudio será enviado para novos contatos nesta lista'
                    : 'Grave um áudio para habilitar esta ferramenta'}
                </small>
              </div>

              <div className="initial-message-config">
                <div className="form-group">
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-add-followup-rule"
                      onClick={startAudioRecording}
                      disabled={audioUploading || audioRecording}
                    >
                      {audioRecording ? 'Gravando...' : 'Gravar áudio'}
                    </button>

                    <button
                      type="button"
                      className="btn-cancel-form"
                      onClick={stopAudioRecording}
                      disabled={audioUploading || !audioRecording}
                    >
                      Parar
                    </button>

                    <button
                      type="button"
                      className="btn-delete-rule"
                      onClick={handleRemoveAudio}
                      disabled={audioUploading || (!initialAudioUrl && audioRecording)}
                    >
                      Remover áudio
                    </button>
                  </div>
                </div>

                {audioUploading && (
                  <small className="toggle-description">Salvando áudio...</small>
                )}

                {(initialAudioUrl || audioPreviewUrl) && (
                  <div className="message-preview">
                    <label>Preview:</label>
                    <div className="preview-box">
                      <audio
                        controls
                        src={audioPreviewUrl || initialAudioUrl}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                )}

                <div className="block-agent-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={blockAgentResponseForInitialAudio}
                      onChange={(e) => setBlockAgentResponseForInitialAudio(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      Bloquear resposta do agente IA
                    </span>
                  </label>
                  <small className="toggle-description">
                    {blockAgentResponseForInitialAudio
                      ? '⚠️ O agente NÃO responderá quando este áudio for enviado.'
                      : '✅ O agente responderá normalmente após o áudio ser enviado.'}
                  </small>
                </div>
              </div>
            </div>

            {/* Seção de Formulário */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="description" />
                  <h4>Configuração de Formulário</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formEnabled}
                    onChange={(e) => handleFormToggle(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {formEnabled 
                      ? '✅ Formulário ativo' 
                      : '❌ Formulário inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {formEnabled 
                    ? 'Um formulário será enviado quando um contato entrar nesta lista'
                    : 'Nenhum formulário será enviado'}
                </small>
              </div>

              {formEnabled && (
                <div className="initial-message-config">
                  {/* Adicionar novo campo */}
                  <div className="form-group">
                    <label>Nome do campo:</label>
                    <input
                      type="text"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      placeholder="Ex: CPF, Data de Nascimento, Telefone"
                      style={{ 
                        width: '100%',
                        padding: '0.75rem',
                        height: '40px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px',
                        fontSize: '0.95rem',
                        fontFamily: 'inherit',
                        backgroundColor: 'var(--input-background)',
                        color: 'var(--text-color)',
                        boxSizing: 'border-box'
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddField();
                        }
                      }}
                    />
                    
                    {/* Selecionar tipo de campo */}
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.75rem' }}>
                      <label style={{ fontSize: '0.9rem', color: 'var(--text-color-secondary)' }}>Tipo:</label>
                      <select
                        value={newFieldType}
                        onChange={(e) => setNewFieldType(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--input-background)',
                          color: 'var(--text-color)',
                          fontSize: '0.9rem'
                        }}
                      >
                        {FIELD_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Controles em baixo */}
                    <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginTop: '0.75rem' }}>
                      <label className="toggle-label" style={{ margin: 0, flex: 1 }}>
                        <input
                          type="checkbox"
                          checked={newFieldRequired}
                          onChange={(e) => setNewFieldRequired(e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                        <span className="toggle-text">Obrigatório</span>
                      </label>
                      <button
                        type="button"
                        className="btn-add-followup-rule"
                        onClick={handleAddField}
                        style={{ marginBottom: 0, padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                      >
                        <AppIcon name="add" style={{ fontSize: '1rem' }} />
                        Adicionar
                      </button>
                    </div>
                  </div>

                  {/* Lista de campos configurados */}
                  {formFields.length > 0 && (
                    <div className="followup-rules-container">
                      <div className="followup-rules-label">Campos Configurados ({formFields.length}):</div>
                      <div className="followup-rules-list">
                        {formFields.map((field) => (
                          <div key={field.id} className="followup-rule-item">
                            <div className="rule-info">
                              <div className="rule-number">
                                <AppIcon name={field.icon === 'schedule' ? 'schedule' : 'text_fields'} style={{ fontSize: '1rem', marginRight: '0.5rem' }} />
                                {field.name}
                              </div>
                              <div className="rule-details">
                                <span className="rule-target">
                                  {field.type === 'datetime' ? '📆 Agendamento' : '📝 Texto'}
                                  {' • '}
                                  {field.required ? '✅ Obrigatório' : '⭕ Opcional'}
                                </span>
                              </div>
                            </div>
                            <div className="rule-actions">
                              <button
                                className={`btn-toggle-rule ${field.required ? 'active' : 'inactive'}`}
                                onClick={() => handleToggleFieldRequired(field.id)}
                                title={field.required ? 'Marcar como opcional' : 'Marcar como obrigatório'}
                              >
                                <AppIcon name={field.required ? 'check_circle' : 'radio_button_unchecked'} />
                              </button>
                              <button
                                className="btn-delete-rule"
                                onClick={() => handleRemoveField(field.id)}
                                title="Remover campo"
                              >
                                <AppIcon name="delete" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {formFields.length === 0 && (
                    <div className="info-box">
                      <AppIcon name="info" />
                      <p>
                        Nenhum campo configurado ainda. Adicione campos acima para criar o formulário que será enviado aos contatos.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Seção de Configurações de Agendamento */}
            {formEnabled && formFields.some(f => f.type === 'datetime') && (
              <div className="crm-section">
                <div className="section-header">
                  <div className="header-left">
                    <AppIcon name="event_available" />
                    <h4>Configurações de Agendamento</h4>
                  </div>
                </div>

                <div className="tool-enable-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={scheduleSettings.enabled}
                      onChange={(e) => handleScheduleToggle(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      {scheduleSettings.enabled 
                        ? '✅ Verificação de disponibilidade ativa' 
                        : '❌ Verificação de disponibilidade inativa'}
                    </span>
                  </label>
                  <small className="toggle-description">
                    {scheduleSettings.enabled 
                      ? 'O sistema verificará se o horário está disponível e respeitará o limite máximo de agendamentos simultâneos'
                      : 'O sistema permitirá agendamentos sem verificar disponibilidade'}
                  </small>
                </div>

                <div className="section-content">
                  {/* Dias de Atendimento */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                      Dias de Atendimento
                    </label>
                    <select
                      value={scheduleSettings.workDays}
                      onChange={(e) => setScheduleSettings({ ...scheduleSettings, workDays: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--text-color)'
                      }}
                    >
                      {WORK_DAYS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                        Início do Atendimento
                      </label>
                      <input
                        type="time"
                        value={scheduleSettings.startTime}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, startTime: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-color)',
                          color: 'var(--text-color)'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                        Fim do Atendimento
                      </label>
                      <input
                        type="time"
                        value={scheduleSettings.endTime}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, endTime: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-color)',
                          color: 'var(--text-color)'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                        Duração por Agendamento (minutos)
                      </label>
                      <select
                        value={scheduleSettings.durationMinutes}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, durationMinutes: parseInt(e.target.value) })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-color)',
                          color: 'var(--text-color)'
                        }}
                      >
                        <option value={15}>15 minutos</option>
                        <option value={30}>30 minutos</option>
                        <option value={45}>45 minutos</option>
                        <option value={60}>1 hora</option>
                        <option value={90}>1h30</option>
                        <option value={120}>2 horas</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.85rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                        Máx. Agendamentos por Horário
                      </label>
                      <select
                        value={scheduleSettings.maxConcurrent}
                        onChange={(e) => setScheduleSettings({ ...scheduleSettings, maxConcurrent: parseInt(e.target.value) })}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--bg-color)',
                          color: 'var(--text-color)'
                        }}
                      >
                        <option value={1}>1 agendamento</option>
                        <option value={2}>2 agendamentos</option>
                        <option value={3}>3 agendamentos</option>
                        <option value={4}>4 agendamentos</option>
                        <option value={5}>5 agendamentos</option>
                      </select>
                    </div>
                  </div>

                  <div className="info-box" style={{ marginTop: '1rem' }}>
                    <AppIcon name="info" />
                    <p>
                      O agente verificará se o horário está disponível antes de confirmar o agendamento.
                      Horário de atendimento: {scheduleSettings.startTime} às {scheduleSettings.endTime}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Seção de Gatilhos */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="bolt" />
                  <h4>⚡ Gatilhos (Triggers)</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={triggersEnabled}
                    onChange={(e) => handleTriggersToggle(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">Habilitar Gatilhos</span>
                </label>
                <p className="tool-description">
                  Configure palavras-chave que, quando aparecerem nas mensagens, movem automaticamente o card para esta lista.
                </p>
              </div>

              {triggersEnabled && (
                <TriggersConfigTab 
                  listId={list?.id} 
                  projectId={projectId}
                  enabled={triggersEnabled}
                  onSave={() => console.log('Gatilhos salvos')}
                />
              )}
            </div>

            {/* Seção de Notificação em Grupo */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="group" />
                  <h4>Notificação de Formulário em Grupo</h4>
                </div>
              </div>

              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={groupNotificationEnabled}
                    onChange={(e) => setGroupNotificationEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {groupNotificationEnabled 
                      ? '✅ Notificação ativa' 
                      : '❌ Notificação inativa'}
                  </span>
                </label>
                <small className="toggle-description">
                  {groupNotificationEnabled 
                    ? 'Quando um lead entrar nesta lista, uma notificação será enviada para o grupo com os dados do formulário'
                    : 'Nenhuma notificação será enviada para o grupo'}
                </small>
              </div>

              {groupNotificationEnabled && (
                <div className="initial-message-config">
                  {/* Botão para carregar grupos */}
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn-add-followup-rule"
                      onClick={loadGroups}
                      disabled={loadingGroups}
                      style={{ marginBottom: '1rem' }}
                    >
                      {loadingGroups ? 'Carregando...' : 'Carregar Grupos'}
                    </button>
                  </div>

                  {/* Lista de grupos disponíveis */}
                  {availableGroups.length > 0 && (
                    <div className="form-group">
                      <label style={{ fontSize: '0.9rem', color: 'var(--text-color-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                        Selecione um grupo:
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                        {availableGroups.map((group) => (
                          <div
                            key={group.jid}
                            onClick={() => setSelectedGroup({ jid: group.jid, name: group.name })}
                            style={{
                              padding: '0.75rem',
                              borderRadius: '6px',
                              border: `1px solid ${selectedGroup?.jid === group.jid ? 'var(--accent-color)' : 'var(--border-color)'}`,
                              background: selectedGroup?.jid === group.jid ? 'rgba(6, 182, 212, 0.1)' : 'var(--card-background)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <AppIcon name={selectedGroup?.jid === group.jid ? 'check_circle' : 'radio_button_unchecked'} style={{ fontSize: '1.2rem', color: selectedGroup?.jid === group.jid ? 'var(--accent-color)' : 'var(--text-color-secondary)' }} />
                            <span style={{ flex: 1 }}>{group.name}</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-color-secondary)' }}>
                              {group.participant_count} participantes
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {availableGroups.length === 0 && !loadingGroups && (
                    <div className="info-box">
                      <AppIcon name="info" />
                      <p>
                        Clique em "Carregar Grupos" para buscar os grupos disponíveis na conexão do projeto.
                      </p>
                    </div>
                  )}

                  {/* Grupo selecionado */}
                  {selectedGroup && (
                    <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(6, 182, 212, 0.1)', borderRadius: '6px', border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AppIcon name="check_circle" style={{ color: 'var(--accent-color)' }} />
                        <span style={{ fontWeight: 500 }}>Grupo selecionado: {selectedGroup.name}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="crm-modal-sidebar">
            <h4>Informações</h4>
            
            {/* Informações da conexão */}
            {connection && (
              <div className="info-box-sidebar">
                <div className="info-item">
                  <AppIcon name="phone" />
                  <div>
                    <strong>Conexão</strong>
                    <p>{connection.name}</p>
                    <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{connection.number}</p>
                  </div>
                </div>
              </div>
            )}

            {list && (
              <div className="info-box-sidebar">
                <div className="info-item">
                  <AppIcon name="credit_card" />
                  <div>
                    <strong>Cards</strong>
                    <p>{list?.cards?.length || 0} itens</p>
                  </div>
                </div>
                
                <div className="info-item">
                  <AppIcon name="schedule" />
                  <div>
                    <strong>Criada em</strong>
                    <p>{list?.created_at ? new Date(list.created_at).toLocaleDateString('pt-BR') : 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="crm-modal-footer">
          <button className="btn-cancel-crm" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save-crm" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
